/**
 * ScrollableWeekStrip — React (web) implementation.
 *
 * Renders several weeks flattened into ONE horizontally-scrolling
 * `role="radiogroup"` of DateChip buttons with roving tabindex (one focusable
 * button at a time; arrow / Home / End keys move focus per ADR-025). Pressing a
 * day calls onSelectDate(day.date); holds no selection state. No raw hex values
 * or pixel literals in this file.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DateChip, type WebDateChipVariant } from '../DateChip/DateChip.web';
import type { WeekStripDayInput } from '../WeekStrip/WeekStrip';
import {
  WEEK_STRIP_GAP,
  WEEK_STRIP_PADDING_VERTICAL,
  resolveWeekStripDayState,
  resolveWeekStripDayTestID,
} from '../WeekStrip/WeekStrip';
import { sizing } from '../../elements/sizing';
import { SCREEN_GUTTER, spacing } from '../../elements/spacing';

/**
 * The narrowest a `picker` day column gets before the strip scrolls. v7's
 * Web/Date chip draws five days across the 354px phone column, about 64px
 * each; there is no chip-width token, so it is composed from tokens.
 */
const WEB_PICKER_DAY_MIN_WIDTH = sizing.touchTarget.minimum + spacing.m;

export interface WebScrollableWeekStripProps {
  weeks: WeekStripDayInput[][];
  selectedDate?: string;
  onSelectDate?: (date: string) => void;
  'data-testid'?: string;
  accessibilityLabel?: string;
  /** Full-bleed to the host gutter; default false keeps the root margin-free. */
  bleed?: boolean;
  /** Analytics id rendered on every day chip; matches the native default. */
  actionId?: string;
  /**
   * Web only. `strip` (default) is the app's week strip; `picker` draws the web
   * form's day picker chips as equal columns that share the row.
   */
  variant?: WebDateChipVariant;
}

export function ScrollableWeekStrip({
  weeks,
  selectedDate,
  onSelectDate,
  'data-testid': testId,
  accessibilityLabel,
  bleed = false,
  actionId = 'ui.scrollable-week-strip.change',
  variant = 'strip',
}: WebScrollableWeekStripProps) {
  const days = weeks.flat();
  const refs = useRef<(HTMLElement | null)[]>([]);
  const selectedIndex = days.findIndex(
    (d) => resolveWeekStripDayState(d.date, d.state, selectedDate) === 'selected',
  );
  const [focusIndex, setFocusIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  // Keep the roving tab stop valid as the host changes selection or the day list
  // after mount: prefer the selected day, else keep the current index but clamp
  // it into range. Keys on days.length so a shrink re-runs even when
  // selectedIndex stays -1.
  useEffect(() => {
    setFocusIndex((prev) => {
      if (selectedIndex >= 0) return selectedIndex;
      if (days.length === 0) return 0;
      return Math.min(prev, days.length - 1);
    });
  }, [selectedIndex, days.length]);

  const move = useCallback(
    (next: number) => {
      if (days.length === 0) return;
      const clamped = (next + days.length) % days.length;
      setFocusIndex(clamped);
      refs.current[clamped]?.focus();
    },
    [days.length],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(focusIndex + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(focusIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(days.length - 1);
        break;
      default:
        break;
    }
  };

  // The picker's days share the row as equal columns, never narrower than
  // WEB_PICKER_DAY_MIN_WIDTH; past that the strip scrolls as the default does.
  const layout: React.CSSProperties =
    variant === 'picker'
      ? {
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: `minmax(${WEB_PICKER_DAY_MIN_WIDTH}px, 1fr)`,
        }
      : { display: 'flex', flexDirection: 'row', alignItems: 'flex-start' };
  const containerStyle: React.CSSProperties = {
    ...layout,
    gap: WEEK_STRIP_GAP,
    paddingTop: WEEK_STRIP_PADDING_VERTICAL,
    paddingBottom: WEEK_STRIP_PADDING_VERTICAL,
    // One continuous horizontal scroller across all weeks (scroll, never clip).
    overflowX: 'auto',
    // Opt-in full-bleed: cancel the host's horizontal gutter so chips peek off
    // the edge, re-insetting the content so the first chip aligns with
    // surrounding cards. Off by default so the root stays margin-free (ADR-020 R2).
    ...(bleed
      ? {
          paddingLeft: SCREEN_GUTTER,
          paddingRight: SCREEN_GUTTER,
          marginLeft: -SCREEN_GUTTER,
          marginRight: -SCREEN_GUTTER,
        }
      : {}),
  };

  return (
    <div
      role="radiogroup"
      aria-label={accessibilityLabel}
      style={containerStyle}
      data-testid={testId}
      onKeyDown={onKeyDown}
    >
      {days.map((day, index) => (
        <DateChip
          actionId={actionId}
          key={day.date}
          ref={(el) => {
            refs.current[index] = el;
          }}
          dayLabel={day.dayLabel}
          weekdayLabel={day.weekdayLabel}
          state={resolveWeekStripDayState(day.date, day.state, selectedDate)}
          today={day.today}
          todayAccessibilityLabel={day.todayAccessibilityLabel}
          marker={day.marker}
          markerAccessibilityLabel={day.markerAccessibilityLabel}
          accessibilityHint={day.accessibilityHint}
          badgeLabel={day.badgeLabel}
          variant={variant}
          disabled={day.selectable === false}
          onPress={onSelectDate ? () => onSelectDate(day.date) : undefined}
          tabIndex={index === focusIndex ? 0 : -1}
          data-testid={resolveWeekStripDayTestID(day, testId)}
        />
      ))}
    </div>
  );
}
